
import { useSession } from "@supabase/auth-helpers-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { ProfileFormValues } from "@/components/settings/ProfileForm";

export const useProfile = () => {
  const session = useSession();
  const queryClient = useQueryClient();

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["profile", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;

      console.log("Fetching profile for user:", session.user.id);

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }
      
      // If no profile exists, create one
      if (!data && session?.user) {
        console.log("Creating profile for user:", session.user.id);
        const { data: newProfile, error: createError } = await supabase
          .from("profiles")
          .insert({
            id: session.user.id,
            email: session.user.email,
            first_name: "",
            last_name: "",
            plan: "UNLIMITED"
          })
          .select()
          .single();

        if (createError) {
          console.error("Error creating profile:", createError);
          throw createError;
        }
        
        return newProfile;
      }
      
      if (data?.preferred_language) {
        localStorage.setItem('preferred_language', data.preferred_language);
        document.documentElement.lang = data.preferred_language;
      }
      
      console.log("Profile data loaded:", data);
      return data;
    },
    enabled: !!session?.user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const updateProfile = useMutation({
    mutationFn: async (values: ProfileFormValues) => {
      if (!session?.user?.id) {
        throw new Error("No user session found");
      }

      const updateData = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone_number: values.phone_number,
      };

      console.log("Updating profile with data:", updateData);

      // Update profile data
      const { data, error: profileError } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("id", session.user.id)
        .select()
        .single();

      if (profileError) {
        console.error("Profile update error:", profileError);
        throw profileError;
      }

      console.log("Profile updated successfully:", data);

      // Update auth email if it changed
      if (values.email !== profile?.email && session?.user?.email !== values.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: values.email
        });
        
        if (emailError) {
          console.warn("Could not update auth email:", emailError);
          // Don't throw here as profile update succeeded
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Perfil actualizado exitosamente");
    },
    onError: (error) => {
      console.error("Error updating profile:", error);
      toast.error("Error al actualizar el perfil");
    },
  });

  return {
    profile,
    isLoadingProfile,
    updateProfile,
  };
};
